import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TextField, MenuItem, Box, Autocomplete, Button, FormControl, InputLabel, Select, CircularProgress } from "@mui/material";
import { WORKFLOW_SOURCE_FIELD_NAME, WORKFLOW_SOURCE_RECORD_NAME } from "../../constants/loopRecordOptions";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../redux/api";
import { useTranslation } from "react-i18next";
import { eachDeep } from "deepdash-es/standalone";
import { FolderOpenOutlined, MoreHorizOutlined } from "@mui/icons-material"
import WorkflowSessionModal1 from "../../components/ItemProperties/WorkflowSessionModal1";
import WorkflowSessionModal from "../../components/ItemProperties/WorkflowSessionModal";
import { cloneDeep, debounce, set } from "lodash";
import { ParentScreenCtx } from "../../pages/workbench_build/WorkbenchBuild";
import { treeActions } from "../../redux/slices/treeSlice";
import { PatternControl } from "../../components/ItemProperties/Forms/PatternControl";
import LabeledCheckbox from "../../components/TemplateProperties/TPCheckbox";


const WorkflowSourceType = ({ node, valuesHandler, variableName, onlySessionData, enableWorkflowItemDependencyOption, showTimeout, obLabel = "", addConstantControl, hideSourceTypeOptions, enableTradingPartnerControl, compareValueApplicable, disabled, workflowItemLabel, sourceNameLabel, source, from }) => {

  const { t } = useTranslation();
  
  // Safe translation function that falls back to original value if translation doesn't exist
  const safeTranslate = (key) => {
    if (!key) return key;
    try {
      const translated = t(key);
      // If translation returns the key itself (meaning no translation found), return original
      return translated === key ? key : translated;
    } catch (error) {
      return key;
    }
  };
  const parentScreen = useContext(ParentScreenCtx)
  const { account, user } = useSelector(state => state?.queryParams || {});
  const [sourceRecord, setSourceRecord] = useState("SELECT");
  const [workflowItem, setWorkflowItem] = useState({});
  const [outboundCondition, setOutboundCondition] = useState("");
  const [sourceFieldNameOptions, setSourceFieldNameOptions] = useState([]);
  const { leftTree, templateChanged } = useSelector(state => state.treeData[parentScreen]);
  const [workflowItemsOptions, setWorkflowItemsOptions] = useState([]);
  const [outboundItemsOptions, setOutboundItemsOptions] = useState([]);
  const [workflowGroupOptions, setWorkflowGroupOptions] = useState([]);
  const [sourceFileDirectory, setSourceFileDirectory] = useState("");
  const [sourceFileMask, setSourceFileMask] = useState("");
  const [workflowGroup, setWorkflowGroup] = useState("");
  const [preserveDirectoryStructure, setPreserveDirectoryStructure] = useState(false);
  const [doNotDelete, setDoNotDelete] = useState(false);
  const [fileCountFilter, setFileCountFilter] = useState("");
  const [template, setTemplate] = useState("");
  const [sourceName, setSourceName] = useState("")
  const [workflowSessionModal, setWorkflowSessionModal] = useState(false);
  const leftTreeSelectedNode = useSelector((state) => state.treeData.workflow.leftTree.tree.body.selectedNode);
  const leftTranslatedData = useSelector((state) => state.treeData.outbound.leftTree.tree.body.translatedData);
  const [enableTranslatedData, setEnableTranslatedData] = useState(false);
  const [constantValue, setConstantValue] = useState("");
  const [inboundPattern, setInboundPattern] = useState("");
  const booleanCompareValue = [
      "DATA_FOUND",
      "DATA_SENT", 
      "TRANSLATION_ERRORS",
      "COMMUNICATION_ERROR",
      "VALIDATION_ERRORS",
      "ELEM_VAL_ERR",
      "TRN_SET_VAL_ERR", 
      "FNCT_GRP_VAL_ERR",
      "SEG_VAL_ERR",
      "PROCESSING_ERRORS",
    ];
  const { translatedData, selectedNode } = useSelector(
    (state) => state?.treeData?.workflow?.rightTree?.tree.body || []
  );
  const [timeout, setTimeoutValue] = useState('');
  const [timeoutModal, setTimeoutModal] = useState({ open: false, isFirstTime: false });
  const [sourceDirectoryModal, setSourceDirectoryModal] = useState({ open: false, isFirstTime: false });
  const [sourceFileMaskModal, setSourceFileMaskModal] = useState({ open: false, isFirstTime: false });
  const [sessionData, setSessionData] = useState([]);


  const dispatch = useDispatch();
  let SOURCE_TYPE_OPTIONS = (from === "WF_TRACKING_REPORT_PROPERTIES"  || from ===  "VARIABLE_PROPERTIES") ? 
  [...WORKFLOW_SOURCE_RECORD_NAME].map((e) => {
    if (e.value === "ERR_VALIDATION") {
      return { ...e, value: "VALIDATION_ERRORS" }
    }
    return e;
  }) 
  : [...WORKFLOW_SOURCE_RECORD_NAME];

  if (hideSourceTypeOptions?.length) {
    SOURCE_TYPE_OPTIONS = SOURCE_TYPE_OPTIONS.filter(e => !hideSourceTypeOptions.includes(e.value))
  }

  if (enableTradingPartnerControl) {
    const obj = { value: "TRADING_PARTNER_KEYS", title: t("TRADING_PARTNER_KEYS") }
    SOURCE_TYPE_OPTIONS.splice(8, 0, obj);
  }

  const handleShowOutboundTemplate = useCallback((outboundCondition, workflowItem) => {
    let found = false;
    eachDeep(translatedData, (workflow) => {
      if (found) return;
      if (workflow?.key && workflow?.ICON_DISPLAY === "TRANSLATE" && workflow?.NAME === workflowItem?.value) {
        found = true;
        workflow.CONDITIONAL_TEMPLATE.forEach(e => {
          e?.OUTBOUND_CONDITION.forEach(e => {
            if (e?.OUTBOUND_NAME === outboundCondition) {
              dispatch(treeActions.setCurrentScreen("outbound"));
              dispatch(treeActions.setForceTemplate({
                workflowGroup: e?.TEMPLATE_LOCATION?.split("/")?.[1],
                template: e?.TEMPLATE?.endsWith('.json') ? e?.TEMPLATE?.slice(0, -5) : e?.TEMPLATE,
                screen: "outbound"
              }));
            }
          })
        })
        
      }
    })
  }, [outboundCondition, workflowItem])

  const getWorkflowOptions = useCallback(() => {
    const workflowOptions = [t("SELECT")];
    leftTranslatedData.forEach(e => {
      workflowOptions.push(e.title);
    })
    setWorkflowGroupOptions([...workflowOptions]);
  }, [leftTranslatedData])

  const templateOptions = useMemo(() => {
    const templateOptions = [t("SELECT")];
    leftTranslatedData.forEach(e => {
      if (e?.title === workflowGroup) {
        e?.children?.forEach(o => {
          if (o?.type !== "DOCUMENT") templateOptions.push(o.title);
        })
      }
    })
    return templateOptions;
  }, [workflowGroup])

  if (addConstantControl) {
    const obj = { value: "CONSTANT", title: t("CONSTANT") }
    SOURCE_TYPE_OPTIONS.splice(3, 0, obj);
  }

  const isGreaterNode = (nodeKey) => {
    if (!selectedNode?.key || !nodeKey) return false;

    const selectedKeyParts = selectedNode.key.split("-").slice(1).map(Number);
    const nodeKeyParts = nodeKey.split("-").slice(1).map(Number);

    // Compare key parts to determine if node is below selected node in the tree
    const minLength = Math.min(selectedKeyParts.length, nodeKeyParts.length);

    for (let i = 0; i < minLength; i++) {
      if (nodeKeyParts[i] > selectedKeyParts[i]) return true;
      if (nodeKeyParts[i] < selectedKeyParts[i]) return false;
    }

    // If we get here and nodeKey has more parts, it's a descendant
    return nodeKeyParts.length > selectedKeyParts.length;
  };

  const handleViewTemplateClick = () => {
    dispatch(treeActions.setCurrentScreen("outbound"));
    dispatch(treeActions.setForceTemplate({
      workflowGroup: workflowGroup,
      template: template,
      screen: "outbound"
    }));
  };


  useEffect(() => {
    if (WORKFLOW_SOURCE_FIELD_NAME[sourceRecord]) {
      const options = [...WORKFLOW_SOURCE_FIELD_NAME[sourceRecord]]
      if ((source === "CONDITION_IF" || source === "LOOP") && sourceRecord === "ERR_VALIDATION") {
        options.push({ value: "MAP_ERROR_EXIST", title: t("MAP_ERROR_EXIST") });
      }
      setSourceFieldNameOptions(
        options.map((ele, index) => {
          return (
            <MenuItem key={index} value={ele.value}>
              {ele.title}
            </MenuItem>
          );
        })
      );
    }
    else if (["VARIABLE", "PLUGIN"].includes(sourceRecord)) {
      const valueToIcon = {
        PLUGIN: parentScreen === "workflow" ? "CALL_PLUGIN" : "PLUGIN",
        VARIABLE: "VARIABLE_ITEM",
      };
      const options = [{ title: t("SELECT"), value: t("SELECT") }]
      eachDeep(translatedData, (obj) => {
        if (
          obj?.key &&
          (obj?.ICON_DISPLAY === valueToIcon[sourceRecord])
        ) {
          if (obj?.ICON_DISPLAY === valueToIcon.PLUGIN && valueToIcon[sourceRecord] === valueToIcon.PLUGIN) {
            const alreadyExist =
              options.filter((ele) => (obj?.OUT_ARG?.map(e => e?.MAPNAME) || []).includes(ele.value))
                .length >= 1;
            if (!alreadyExist) {
              options.push(...(obj?.OUT_ARG || []).map(e => ({ title: e?.MAPNAME, value: e?.MAPNAME })));
            }
          }
          if (obj?.ICON_DISPLAY === valueToIcon.VARIABLE && valueToIcon[sourceRecord] === valueToIcon.VARIABLE) {
            const argName = obj?.NAME;
            const alreadyExist =
              options.filter((ele) => ele.value === argName).length >= 1;
            if (!alreadyExist) {
              options.push({ title: argName, value: argName });
            }
          }
        }
      });

      const menuOptions = options.map((ele, index) => {
        return (
          <MenuItem key={index} value={ele.value}>
            {ele.title}
          </MenuItem>
        );
      });
      setSourceFieldNameOptions(menuOptions);
    }
    else if (['ACCOUNT_KEYS', "TRADING_PARTNER_KEYS"].includes(sourceRecord)) {
      const apiEndpoints = {
        'ACCOUNT_KEYS': api.endpoints.getAccountData,
        "TRADING_PARTNER_KEYS": api.endpoints.getTradingPartnersKeys
      }
      dispatch(apiEndpoints?.[sourceRecord]?.initiate({
        account
      }, { forceRefetch: true }))
        .then(res => {
          if (res?.data?.current_state === "SUCCESS") {
            let data;

            if (res?.data?.data?.account_data_keys) {
              data = res?.data?.data?.account_data_keys.map(
                (o) => Object?.keys(o)?.[0]
              );
            }
            else if (res?.data?.data?.trading_partner_keys) {
              data = res?.data?.data?.trading_partner_keys.map(
                (o) => o
              ).filter(e => e);
            }
            if (data) {
              const newData = [t("SELECT"), ...data];
              const options = newData.map((opt, idx) => (
                <MenuItem key={`${opt}-${idx}`} value={opt}>
                  {opt}
                </MenuItem>
              ))
              setSourceFieldNameOptions(options);
            }
          }
        })
    }
    else if (sourceRecord === "SESSION_DATA") {
      const items = ["VARIABLE_ITEM", "GET_DATA", "TRANSLATE", "SEND_DATA", "LOOP_ITEM", "CONDITION_IF", "CALL_PLUGIN", "CALL_WORKFLOW", "TRADING_PARTNER_LOOKUP", "ACTION", "ERROR_HANDLER", "TRACKING_REPORT_ITEM"]
      let options = [];
      eachDeep(translatedData, (obj) => {
        if (
          obj?.key &&
          (obj?.ICON_DISPLAY) && items.includes(obj?.ICON_DISPLAY) && !isGreaterNode(obj?.key)
        ) {
          const alreadyExist =
            options.filter((ele) => (ele.value === obj?.NAME && ele?.isVariable && obj?.ICON_DISPLAY === "VARIABLE_ITEM")).length >= 1;
          if (!alreadyExist) options.push({ label: obj?.NAME, value: obj?.NAME, isTranslatedData: obj?.ICON_DISPLAY === "TRANSLATE", WF_ITEM_ID: obj?.ID, isLoop: obj?.ICON_DISPLAY === "LOOP_ITEM", isVariable: obj?.ICON_DISPLAY === "VARIABLE_ITEM", isCONDITION_IF: obj?.ICON_DISPLAY === "CONDITION_IF", isLoopListValue: obj?.LOOP_TYPE === "LOOP_TYPE_LIST" });
        }
      });
      options = options.sort((a, b) => {
        const labelA = a.label ? a.label.toLowerCase() : '';
        const labelB = b.label ? b.label.toLowerCase() : '';
        if (labelA < labelB) return -1;
        if (labelA > labelB) return 1;
        return 0;
      });
      options = [{ label: t("SELECT"), value: t("SELECT") }, ...options];
      setWorkflowItemsOptions(options);
    } else if (sourceRecord === "WF_ITEM_DEPENDENCY") {
      const items = ["VARIABLE_ITEM", "GET_DATA", "TRANSLATE", "SEND_DATA", "LOOP_ITEM", "CONDITION_IF", "CALL_PLUGIN", "CALL_WORKFLOW", "TRADING_PARTNER_LOOKUP", "ACTION", "ERROR_HANDLER", "TRACKING_REPORT_ITEM"]
      const options = [{ label: t("SELECT"), value: t("SELECT") }];
      eachDeep(translatedData, (obj) => {
        if (
          obj?.key &&
          (obj?.ICON_DISPLAY) && items.includes(obj?.ICON_DISPLAY) && !isGreaterNode(obj?.key) && obj?.CONCURRENT === "TRUE"
        ) {
          options.push({ label: obj?.NAME, value: obj?.NAME, isTranslatedData: obj?.ICON_DISPLAY === "TRANSLATE", WF_ITEM_ID: obj?.ID, isLoop: obj?.ICON_DISPLAY === "LOOP_ITEM", isLoopListValue: obj?.LOOP_TYPE === "LOOP_TYPE_LIST", isVariable: obj?.ICON_DISPLAY === "VARIABLE_ITEM", isCONDITION_IF: obj?.ICON_DISPLAY === "CONDITION_IF" });
        }
      });


      const menuOptions = options.map((opt, idx) => (
        <MenuItem key={`${opt}-${idx}`} value={opt?.value}>
          {opt?.label}
        </MenuItem>
      ))
      setSourceFieldNameOptions(menuOptions);

    }
    else if (sourceRecord === "TRANSLATED_DATA") {
      const items = ["TRANSLATE"]
      let options = [];
      eachDeep(translatedData, (obj) => {
        if (
          obj?.key &&
          (obj?.ICON_DISPLAY) && items.includes(obj?.ICON_DISPLAY) && !isGreaterNode(obj?.key)
        ) {
          const alreadyExist =
            options.filter((ele) => (ele.value === obj?.NAME)).length >= 1;
          if (!alreadyExist) {
            const conditions = [];
            obj?.CONDITIONAL_TEMPLATE?.forEach(e => {
              e?.OUTBOUND_CONDITION?.forEach(e => {
                conditions.push({ name: e?.OUTBOUND_NAME, outboundId: e?.OUTBOUND_ID });
              });
            });
            options.push({
              label: obj?.NAME,
              value: obj?.NAME,
              isTranslatedData:
                obj?.ICON_DISPLAY === "TRANSLATE",
              WF_ITEM_ID: obj?.ID,
              isLoop: obj?.ICON_DISPLAY === "LOOP_ITEM",
              isVariable: obj?.ICON_DISPLAY === "VARIABLE_ITEM",
              isCONDITION_IF: obj?.ICON_DISPLAY === "CONDITION_IF",
              conditons: conditions,
              isLoopListValue: obj?.LOOP_TYPE === "LOOP_TYPE_LIST"
            });
          }
        }
      });
      options = options.sort((a, b) => {
        const labelA = a.label ? a.label.toLowerCase() : '';
        const labelB = b.label ? b.label.toLowerCase() : '';
        if (labelA < labelB) return -1;
        if (labelA > labelB) return 1;
        return 0;
      });
      options = [{ label: t("SELECT"), value: t("SELECT") }, ...options];
      setWorkflowItemsOptions(options);
    }
    else if (sourceRecord === "FORWARD_ONLY_DATA") {
      const items = ["GET_DATA"]
      let options = [];
      eachDeep(translatedData, (obj) => {
        if (
          obj?.key &&
          (obj?.ICON_DISPLAY) && items.includes(obj?.ICON_DISPLAY) && !isGreaterNode(obj?.key) && obj?.INBOUND?.SPECIFY_DECOMPRESSION_DIRECTORY !== "TRUE" && obj?.INBOUND?.SPECIFY_DECOMPRESSION_DIRECTORY !== true
        ) {
          const alreadyExist =
            options.filter((ele) => (ele.value === obj?.NAME && ele?.isVariable && obj?.ICON_DISPLAY === "VARIABLE_ITEM")).length >= 1;
          if (!alreadyExist) {
            const conditions = [];
            obj?.CONDITIONAL_TEMPLATE?.forEach(e => {
              e?.OUTBOUND_CONDITION?.forEach(e => {
                conditions.push(e?.OUTBOUND_NAME);
              });
            });
            options.push({
              label: obj?.NAME,
              value: obj?.NAME,
              isTranslatedData:
                obj?.ICON_DISPLAY === "TRANSLATE",
              WF_ITEM_ID: obj?.ID,
              isLoop: obj?.ICON_DISPLAY === "LOOP_ITEM",
              isVariable: obj?.ICON_DISPLAY === "VARIABLE_ITEM",
              isCONDITION_IF: obj?.ICON_DISPLAY === "CONDITION_IF",
              conditons: conditions,
              isLoopListValue: obj?.LOOP_TYPE === "LOOP_TYPE_LIST"
            });
          }
        }
      });
      options = options.sort((a, b) => {
        const labelA = a.label ? a.label.toLowerCase() : '';
        const labelB = b.label ? b.label.toLowerCase() : '';
        if (labelA < labelB) return -1;
        if (labelA > labelB) return 1;
        return 0;
      });
      options = [{ label: t("SELECT"), value: t("SELECT") }, ...options];
      setWorkflowItemsOptions(options);
    }
    else if (sourceRecord === "STATIC_DATA") getWorkflowOptions();
    else {
      setWorkflowItemsOptions([]);
    }
  }, [sourceRecord])

  useEffect(() => {
    if(!workflowItem || !Object.keys(workflowItem || {}).length || !workflowItemsOptions?.length)
      return;
    
    if (sourceRecord === "WF_ITEM_DEPENDENCY") return;
    if (!sourceRecord)return;
    if (WORKFLOW_SOURCE_FIELD_NAME[sourceRecord]){
      const options = [...WORKFLOW_SOURCE_FIELD_NAME[sourceRecord]]
      if ((source === "CONDITION_IF" || source === "LOOP") && sourceRecord === "ERR_VALIDATION") {
        options.push({ value: "MAP_ERROR_EXIST", title: t("MAP_ERROR_EXIST") });
      }
      setSourceFieldNameOptions(
        options.map((ele, index) => {
          return (
            <MenuItem key={index} value={ele.value}>
              {ele.title}
            </MenuItem>
          );
        })
      );
      return;
    };

    if (workflowItem?.isLoop) {
      setSourceFieldNameOptions([
        <MenuItem value={"LOOP_COUNT"} key={`session-data-source-name-${"LOOP_COUNT"}`}>
          {t("LOOP_COUNT")}
        </MenuItem>
        , ...((workflowItem?.isLoopListValue) ? [<MenuItem value={"LOOP_LIST_VALUE"} key={`session-data-source-name-${"LOOP_LIST_VALUE"}`}>
          {"LOOP_LIST_VALUE"}
        </MenuItem>] : [])
      ]);
    }
    else if (workflowItem?.isVariable || workflowItemsOptions?.find(e => e?.value === workflowItem?.value)?.isVariable) {
      setSourceFieldNameOptions([
        <MenuItem value={"VALUE"} key={`session-data-source-name-${"VALUE"}`}>
          {t("VALUE")}
        </MenuItem>
      ])
    }
    else if (workflowItem?.isCONDITION_IF || workflowItemsOptions.find(e => e?.value === workflowItem?.value)?.isCONDITION_IF) {
      setSourceFieldNameOptions([])
    }
    else if (sourceRecord === "TRANSLATED_DATA") {
      setOutboundItemsOptions(workflowItem?.conditons?.map(e =>
        <MenuItem
          value={e?.name}
          key={`session-data-source-name-${e?.name}`}
        >
          {t(e?.name)}
        </MenuItem>
      ) || []);
    }
    else {
      
      dispatch(api.endpoints.getSessionData.initiate({
        account: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_ACCOUNT : account,
        user,
        workflow_group_name: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_GROUP : leftTreeSelectedNode?.workflowGroup,
        workflow_name: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW : leftTreeSelectedNode?.title,
        source_record_name: sourceRecord?.toLowerCase(),
        wf_item: node?.RECORD_TYPE === "CALL_WORKFLOW_DATA" ? (workflowItem?.value?.split("\\")?.[1] || "") : workflowItem?.value || "",
        outbound_condition_name:node?.RECORD_TYPE === "SESSION_DATA" ? (workflowItem?.isTranslatedData ? outboundCondition || "" : "") : outboundCondition || "",
        variable_name: variableName || ""
      }, { forceRefetch: true }))
        .then(res => {
          if (workflowItem?.isCONDITION_IF || workflowItemsOptions.find(e => e?.value === workflowItem?.value)?.isCONDITION_IF) {
            setSourceFieldNameOptions([])
            return;
          }
          if (res?.data?.current_state === "SUCCESS") {
            let data = cloneDeep(res?.data?.data || []);
            if (sourceName && !data?.includes(sourceName)) {
              data.push(sourceName);
            }
            data = data.sort((a, b) => {
              const labelA = a.label ? a.label.toLowerCase() : '';
              const labelB = b.label ? b.label.toLowerCase() : '';
              if (labelA < labelB) return -1;
              if (labelA > labelB) return 1;
              return 0;
            });
            setSourceFieldNameOptions(data?.map((e, i) => {
              return <MenuItem value={e} key={`session-data-source-name-${i}`}>
                { workflowItem?.value === "Trading Partner Lookup" && e !== "TRADING_PARTNER_FOUND" ? e : t(e) }
              </MenuItem>
            }))
          } else {
            setSourceFieldNameOptions([]);
          }
        })
        .finally(() => {

        })
    }
    // This is For Getting Outbound Condition
    if (workflowItem?.isTranslatedData || node?.OUTBOUND_NAME) {
      dispatch(api.endpoints.getSessionData.initiate({
        account: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_ACCOUNT : account,
        user,
        workflow_group_name: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_GROUP : leftTreeSelectedNode?.workflowGroup,
        workflow_name: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW : leftTreeSelectedNode?.title,
        source_record_name: sourceRecord?.toLowerCase(),
        wf_item: node?.RECORD_TYPE === "CALL_WORKFLOW_DATA" ? (workflowItem?.value?.split("\\")?.[1] || "") : workflowItem?.value || "",
        outbound_condition_name: "",
        variable_name: ""
      }, { forceRefetch: true }))
        .then(res => {
          if (res?.data?.current_state === "SUCCESS") {
            const data = res?.data?.data || [];
            setOutboundItemsOptions(data?.map((e, i) => {
              return <MenuItem value={e} key={`session-data-source-name-${i}`}>
                {t(e)}
              </MenuItem>
            }) || [])
          }
        })
        .finally(() => {

        })
    }
  }, [leftTreeSelectedNode?.key, sourceRecord, workflowItem, outboundCondition, variableName, workflowItemsOptions?.length, templateChanged])

  useEffect(() => {
    if (templateChanged) {
      dispatch(
        treeActions.setLeftTreeContextActionValue({
          screen: "workflow",
          data: {
            name: leftTree.tree.body.selectedNode.title,
            workflowGroup: leftTree.tree.body.selectedNode.workflowGroup,
          },
        })
      );
    }
  }, [leftTreeSelectedNode?.key, sourceRecord])

  useEffect(() => {
    let wfItemFound = {};
    if (node?.WF_ITEM_ID) {
      wfItemFound = workflowItemsOptions?.find((e) => String(e?.WF_ITEM_ID) === String(node?.WF_ITEM_ID));
    }
    if (sourceRecord === "TRANSLATED_DATA") wfItemFound = workflowItemsOptions?.find(e => e?.value  === node?.WFITEM);
    setSourceRecord(node?.RECORD_TYPE || "");
    setWorkflowItem(node?.RECORD_TYPE === "CALL_WORKFLOW_DATA" ? ((node?.WFITEM || workflowItem?.value) ?
      {
        label: (node?.CALL_WORKFLOW || "") + ((node?.CALL_WORKFLOW || "") ? "\\" : "") + (node?.WFITEM || workflowItem?.value?.split("\\")?.[1] || ""),
        value: (node?.CALL_WORKFLOW || "") + ((node?.CALL_WORKFLOW || "") ? "\\" : "") + (node?.WFITEM || workflowItem?.value?.split("\\")?.[1] || ""),
      } : {}) : node?.RECORD_TYPE === "TRANSLATED_DATA" ? {
        label: wfItemFound?.value ? wfItemFound?.value : node?.WFITEM,
        value: wfItemFound?.value ? wfItemFound?.value : node?.WFITEM,
        conditons: wfItemFound?.conditons || [],
        WF_ITEM_ID: node?.WF_ITEM_ID
      } : node?.RECORD_TYPE === "FORWARD_ONLY_DATA" ? {
        label: node?.INBOUND_NAME,
        value: node?.INBOUND_NAME,
      } :{
      ...wfItemFound,
      label: wfItemFound?.value ? wfItemFound?.value : node?.WFITEM,
      value: wfItemFound?.value ? wfItemFound?.value : node?.WFITEM
    })
    if (node?.OUTBOUND_NAME && (node?.RECORD_TYPE === "CALL_WORKFLOW_DATA" || node?.RECORD_TYPE === "SESSION_DATA" || node?.RECORD_TYPE === "TRANSLATED_DATA")) {
      if (node?.RECORD_TYPE === "SESSION_DATA") {
        if (wfItemFound?.isTranslatedData) setEnableTranslatedData(true);
        else setEnableTranslatedData(false);
      }else {
        if (node?.OUTBOUND_NAME) setEnableTranslatedData(true);
        else setEnableTranslatedData(false);
      }

      const matchingCondition = workflowItemsOptions
        ?.find((e) => String(e?.WF_ITEM_ID) === String(node?.WF_ITEM_ID))
        ?.conditons?.find(opt => opt?.outboundId === node?.OUTBOUND_ID)
      if(node?.RECORD_TYPE === "TRANSLATED_DATA" && node?.OUTBOUND_ID === matchingCondition?.outboundId && matchingCondition?.name !== node?.OUTBOUND_NAME) {
        // write code to update the outbound name in redux state via valuesHandler
        valuesHandler({
          OUTBOUND_NAME: matchingCondition?.name,
          RECORD_TYPE: node?.RECORD_TYPE || null,
          CALL_WORKFLOW: null,
          CALL_WORKFLOW_ACCOUNT: null,
          CALL_WORKFLOW_GROUP: null,
          WFITEM: node?.WFITEM || null,
          WF_ITEM_ID: node?.WF_ITEM_ID || null,
          WF_ITEM_LOCATION: node?.WF_ITEM_LOCATION || null,
          WF_ITEM_DEPENDENCY_TIMEOUT: node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
          MAPNAME: node?.MAPNAME || null,
          OUTBOUND_ID: node?.OUTBOUND_ID || null
        }) 
      }
      setOutboundCondition(node?.RECORD_TYPE === "TRANSLATED_DATA" ? matchingCondition?.name || node?.OUTBOUND_NAME : node.OUTBOUND_NAME);
    } else if (wfItemFound?.isTranslatedData) {
      setEnableTranslatedData(true);
      setOutboundCondition(wfItemFound?.conditons?.find(opt => opt?.name === node?.OUTBOUND_NAME)?.name || "");
    }
    else {
      setEnableTranslatedData(false);
      setOutboundCondition("");
    }
    setSourceName(node.MAPNAME || t("SELECT"));
    setTimeoutValue(node.WF_ITEM_DEPENDENCY_TIMEOUT || "");
    setInboundPattern(node?.INBOUND_PATTERN || "");
    setConstantValue(node?.VALUE || "");
    setSourceFileDirectory(node?.SOURCE_FILE_DIRECTORY || "");
    setSourceFileMask(node?.SOURCE_FILE_MASK || "");
    setPreserveDirectoryStructure(node?.SOURCE_FILE_PRESERVE_PATHS === "TRUE");
    setDoNotDelete(node?.SOURCE_FILE_DO_NOT_DELETE === "TRUE");
    setFileCountFilter(node?.FILE_COUNT_FILTER || "");
    const template = node?.TEMPLATE ? node?.TEMPLATE.replace(/\.[^.]+$/, '') : "";
    const workflowGroup = (node?.TEMPLATE_LOCATION || "")?.split("/")[1] || "";
    setTemplate(template);
    setWorkflowGroup(workflowGroup);

  }, [node?.key, workflowItemsOptions, node?.RECORD_TYPE, node?.MAPNAME])

  const debounceHandler = debounce(valuesHandler, 500, { leading: false, trailing: true });

  const mappedOptions = useMemo(() => {
    return sourceFieldNameOptions.map((ele) => ( (["ACCOUNT_NAME", "EMAIL_ADDR"].includes(ele.props.value) && ["SESSION_DATA", "CALL_WORKFLOW_DATA"].includes(sourceRecord)) ? {
      label: ele?.props?.value,
      value: ele?.props?.value,
    } : {
      label: ele?.props?.children,
      value: ele?.props?.value,
    }));
  }, [sourceFieldNameOptions]);


  // Helper function to ensure disabled is a boolean
  const ensureBoolean = (value) => {
    return value === true || value === false ? value : false;
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {onlySessionData ? (
        ""
      ) : (
        <FormControl fullWidth size="small">
          <InputLabel id="source-record-name">
            {t("LBL_SOURCE_RECORD_NAME")}
          </InputLabel>
          <Select
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: "250px",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#555",
                  },
                },
              },
            }}
            size="small"
            labelId="source-record-name"
            disabled={ensureBoolean(disabled) || ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
            label={t("LBL_SOURCE_RECORD_NAME")}
            value={sourceRecord}
            onChange={(e) => {
              const newSourceRecord = e.target.value;
              setSourceRecord(newSourceRecord);
              setOutboundCondition("");
              setWorkflowItem({});
              setSourceName("");
              setWorkflowItemsOptions([]);
              setOutboundItemsOptions([]);
              setSourceFieldNameOptions([]);
              setEnableTranslatedData(false);
              setTimeoutValue("");
              setInboundPattern("");
              setConstantValue("");
              valuesHandler({
                RECORD_TYPE: newSourceRecord,
                CALL_WORKFLOW: null,
                CALL_WORKFLOW_ACCOUNT: null,
                CALL_WORKFLOW_GROUP: null,
                OUTBOUND_NAME: null,
                WFITEM: null,
                WF_ITEM_ID: null,
                WF_ITEM_LOCATION: null,
                MAPNAME: null,
                WF_ITEM_DEPENDENCY_TIMEOUT: null,
                VALUE: null,
                INBOUND_PATTERN: null,
              });
            }}
            >
            {[
              ...SOURCE_TYPE_OPTIONS,
              ...(enableWorkflowItemDependencyOption
                ? [
                  {
                    value: "WF_ITEM_DEPENDENCY",
                    title: t("WF_ITEM_DEPENDENCY"),
                  },
                ]
                : []),
            ].map((e, index) => {
              return (
                <MenuItem
                  value={e.value}
                  key={`source-type-${e.value}-${index}`}>
                  {e.title}
                </MenuItem>
              );
            })}
          </Select>{" "}
        </FormControl>
      )}
      {WORKFLOW_SOURCE_FIELD_NAME[sourceRecord] ||
        sourceRecord === "ACCOUNT_KEYS" ||
        sourceRecord === "TRADING_PARTNER_KEYS" ||
        sourceRecord === "PLUGIN" ||
        sourceRecord === "SELECT" ||
        sourceRecord === "WF_ITEM_DEPENDENCY" ||
        sourceRecord === "CONSTANT" ||
        sourceRecord === "STATIC_DATA" ||
        sourceRecord === "LOCAL_FILE_SYSTEM" ? (
        ""
      ) : (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Autocomplete
            disabled={ensureBoolean(disabled) || ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
            options={workflowItemsOptions}
            getOptionLabel={(option) => option?.label || option?.title || ""}
            isOptionEqualToValue={(option, value) => option?.value === value?.value}
            onChange={(e, val = {}) => {
              setWorkflowItem(val);
              const isTranslated = val?.isTranslatedData || false;
              const isVariable = val?.isVariable || false;
              if(isVariable) {
                setSourceName("VALUE");
              }else {
                setSourceName("");
              }
              setEnableTranslatedData(isTranslated);
              setOutboundCondition("");
              valuesHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW : null,
                CALL_WORKFLOW_ACCOUNT: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_ACCOUNT : null,
                CALL_WORKFLOW_GROUP: sourceRecord === "CALL_WORKFLOW_DATA" ? node?.CALL_WORKFLOW_GROUP : null,
                OUTBOUND_NAME: null,
                WFITEM: (val && val?.value !== t("SELECT")) ? (val?.value || null) : null,
                WF_ITEM_ID: val?.WF_ITEM_ID ? `${val.WF_ITEM_ID}` : null,
                WF_ITEM_LOCATION: val?.value || null,
                MAPNAME: isVariable ? "VALUE" : null,
                VALUE: null,
                INBOUND_PATTERN: null,
                INBOUND_NAME: sourceRecord === "FORWARD_ONLY_DATA" ? val.value : null
              });
            }}
            loading={ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
            loadingText={t("Loading...")}
            fullWidth
            value={workflowItem}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={workflowItemLabel ? workflowItemLabel : t("LBL_WORKFLOW_ITEM")}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {ensureBoolean(leftTree.tree.body.selectedNode.disabled) ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress color="inherit" size={20} />
                        </Box>
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {sourceRecord === "CALL_WORKFLOW_DATA" ? <Button
            disabled={ensureBoolean(disabled)}
            onClick={() => {
              setWorkflowSessionModal(true);
            }} variant="outlined"><MoreHorizOutlined /></Button> : ""}
        </Box>
      )}
      {enableTranslatedData ? (
        <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          disabled={ensureBoolean(disabled) || ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
          select
          value={outboundCondition ?? ""}
          onChange={(e) => {
            const newOutboundCondition = e?.target?.value;
            setOutboundCondition(newOutboundCondition);
            valuesHandler({
              RECORD_TYPE: sourceRecord,
              CALL_WORKFLOW:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW
                  : null,
              CALL_WORKFLOW_ACCOUNT:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW_ACCOUNT
                  : null,
              CALL_WORKFLOW_GROUP:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW_GROUP
                  : null,
              OUTBOUND_NAME: newOutboundCondition,
              WFITEM: workflowItem?.value || null,
              WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                ? `${workflowItem.WF_ITEM_ID}` 
                : null,
              WF_ITEM_LOCATION: workflowItem?.value || null,
              WF_ITEM_DEPENDENCY_TIMEOUT:
                timeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
              MAPNAME: sourceName,
              OUTBOUND_ID: sourceRecord === "TRANSLATED_DATA" ? workflowItem?.conditons?.find(opt => opt?.name === newOutboundCondition)?.outboundId : null
            });
          }}
          size="small"
          label={obLabel || t("LBL_OUT_COND")}
          fullWidth
          InputProps={{
            endAdornment: ensureBoolean(leftTree.tree.body.selectedNode.disabled) ? (
              <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
            ) : null,
          }}>
          {outboundItemsOptions}
        </TextField>
        {sourceRecord === "TRANSLATED_DATA" && <Button
            disabled={!outboundCondition}
            variant="outlined"
            onClick={() => {
              handleShowOutboundTemplate(outboundCondition, workflowItem);
            }}>
            {t("MENU_VIEW")}
          </Button>}
        </Box>
      ) : null}
      
      {(sourceRecord !== "SELECT" &&
        sourceRecord === "CALL_WORKFLOW_DATA" &&
        workflowItem?.value) ||
        (sourceRecord !== "CALL_WORKFLOW_DATA" &&
          sourceRecord !== "SELECT" &&
          sourceRecord !== "CONSTANT") && !["TRANSLATED_DATA", "FORWARD_ONLY_DATA", "STATIC_DATA", "LOCAL_FILE_SYSTEM"].includes(sourceRecord) ? (
        <Autocomplete
          options={mappedOptions}
          disabled={ensureBoolean(disabled) || ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
          isOptionEqualToValue={(option, value) =>
            option.value === (value?.value ?? value)
          }
          onChange={(e, val) => {
            const newSourceName = val?.value || "";
            setSourceName(newSourceName);
            valuesHandler({
              RECORD_TYPE: sourceRecord,
              CALL_WORKFLOW:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW
                  : null,
              CALL_WORKFLOW_ACCOUNT:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW_ACCOUNT
                  : null,
              CALL_WORKFLOW_GROUP:
                sourceRecord === "CALL_WORKFLOW_DATA"
                  ? node?.CALL_WORKFLOW_GROUP
                  : null,
              OUTBOUND_NAME: outboundCondition || null,
              WFITEM: workflowItem?.value?.split("\\")?.[1] || workflowItem?.value?.split("\\")?.[0] || null,
              WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                ? `${workflowItem.WF_ITEM_ID}`
                : node?.WF_ITEM_ID
                ? `${node.WF_ITEM_ID}`
                : null,
              WF_ITEM_LOCATION: workflowItem?.value || null,
              WF_ITEM_DEPENDENCY_TIMEOUT:
                timeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
              MAPNAME: sourceRecord === "TRANSLATED_DATA" ? "SELECT" : newSourceName,
              VALUE: null,
              INBOUND_PATTERN: null,
              ...(booleanCompareValue.includes(newSourceName) &&
                compareValueApplicable
                ? { COMPARE: "TRUE" || node?.COMPARE || null }
                : {}),
            });
          }}
          fullWidth
           value={
             mappedOptions.find((opt) => opt.value === sourceName) ||
             (sourceRecord === "WF_ITEM_DEPENDENCY" 
               ? { label: safeTranslate(sourceName), value: sourceName } 
               : (sourceRecord !== "TRADING_PARTNER_KEYS" &&
                  !["ACCOUNT_NAME"].includes(sourceName) &&
                  ["SESSION_DATA", "CALL_WORKFLOW_DATA"].includes(sourceRecord) &&
                  workflowItem?.value !== "Trading Partner Lookup" || sourceName ===  "TRADING_PARTNER_FOUND"
                    ? { label: safeTranslate(sourceName), value: sourceName }
                    : { label: sourceName, value: sourceName }))
           }
          renderInput={(params) => (
            <TextField
              {...params}
              disabled={ensureBoolean(disabled) || ensureBoolean(leftTree.tree.body.selectedNode.disabled)}
              onChange={(e) => {
                setSourceName(e.target.value);
                valuesHandler({
                  RECORD_TYPE: sourceRecord,
                  CALL_WORKFLOW:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.CALL_WORKFLOW
                      : null,
                  CALL_WORKFLOW_ACCOUNT:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.CALL_WORKFLOW_ACCOUNT
                      : null,
                  CALL_WORKFLOW_GROUP:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.CALL_WORKFLOW_GROUP
                      : null,
                  ...(outboundCondition
                    ? { OUTBOUND_NAME: outboundCondition }
                    : { OUTBOUND_NAME: null }),
                  WFITEM:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.WFITEM?.split("\\")?.[1] || node?.WFITEM?.split("\\")?.[0]
                      : sourceRecord === "SESSION_DATA"
                        ? workflowItem?.value
                        : null,
                  WF_ITEM_ID:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.WFITEM
                      : sourceRecord === "SESSION_DATA"
                      ? workflowItem?.WF_ITEM_ID
                        ? `${workflowItem.WF_ITEM_ID}`
                        : node?.WF_ITEM_ID
                        ? `${node.WF_ITEM_ID}`
                        : null
                      : null,
                  WF_ITEM_LOCATION:
                    sourceRecord === "CALL_WORKFLOW_DATA"
                      ? node?.WFITEM
                      : sourceRecord === "SESSION_DATA"
                      ? workflowItem?.value
                      : null,
                  WF_ITEM_DEPENDENCY_TIMEOUT:
                    timeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
                  MAPNAME: e.target.value,
                  VALUE: null,
                  INBOUND_PATTERN: null,
                });
              }}
              size="small"
              label={sourceNameLabel ? sourceNameLabel : t("LBL_SOURCE_FIELD_NAME")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {ensureBoolean(leftTree.tree.body.selectedNode.disabled) ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                        sx={{ mr: 1 }}
                      />
                    ) : null}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      ) : (
        ""
      )}
      
      {
        sourceRecord === "STATIC_DATA" ?
          <>
            <FormControl fullWidth size="small">
              <InputLabel id="workflow-group-label">{t("LBL_WORKFLOW_GROUP")}</InputLabel>
              <Select
                labelId="workflow-group-label"
                label={t("LBL_WORKFLOW_GROUP")}
                value={workflowGroup}
                onChange={(e) => {
                  setTemplate(t("SELECT"));
                  setWorkflowGroup(e.target.value);
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: "300px",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#888",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#555",
                      },
                    },
                  },
                }}
              >
                {workflowGroupOptions.map((ele, index) => {
                  return (
                    <MenuItem key={index} value={ele}>
                      {ele}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="outbound-template-label">{t("OUTBOUND_TEMPLATE_")}</InputLabel>
                <Select
                  labelId="outbound-template-label"
                  label={t("OUTBOUND_TEMPLATE_")}
                  value={template}
                  MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: "300px",
                      "&::-webkit-scrollbar": {
                        width: "8px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#888",
                        borderRadius: "4px",
                      },
                      "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "#555",
                      },
                    },
                  },
                }}
                  onChange={(e) => {
                    setTemplate(e.target.value);
                    // Compose TEMPLATE_LOCATION dynamically
                    const templateLocation = account && workflowGroup && e.target.value && e.target.value !== t("SELECT")
                      ? `${account}/${workflowGroup}/outbound_templates`
                      : null;
                    valuesHandler({
                      RECORD_TYPE: sourceRecord,
                      TEMPLATE: e?.target.value !== t("SELECT") ? `${e.target.value}.json` : null,
                      TEMPLATE_LOCATION: e?.target?.value !== t("SELECT") ? templateLocation : null,
                      CALL_WORKFLOW: null,
                      CALL_WORKFLOW_ACCOUNT: null,
                      CALL_WORKFLOW_GROUP: null,
                      OUTBOUND_NAME: null,
                      WFITEM: null,
                      WF_ITEM_ID: null,
                      WF_ITEM_LOCATION: null,
                      MAPNAME: null,
                      WF_ITEM_DEPENDENCY_TIMEOUT: null,
                      VALUE: null,
                      INBOUND_PATTERN: null,
                    });
                  }}
                >
                  {templateOptions.map((ele, index) => {
                    return (
                      <MenuItem key={index} value={ele}>
                        {ele}
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
              <Button
                size='small'
                disabled={!workflowGroup || !template}
                variant='outlined'
                sx={{
                  width: "40px",
                  minWidth: "40px",
                  height: "37px"
                }}
                onClick={handleViewTemplateClick}
              >
                {t("MENU_VIEW")}
              </Button>
            </Box>
          </> : <></>
      }

      {
        sourceRecord === "LOCAL_FILE_SYSTEM" ? <>
          <div style={{ display: "flex", gap: 5 }}>
            <TextField
              fullWidth
              size="small"
              label={t("LBL_SOURCE_FILE_DIRECTORY")}
              disabled={ensureBoolean(disabled)}
              value={sourceFileDirectory}
              onChange={(e) => {
                setSourceFileDirectory(e.target.value);
                const values = {
                  RECORD_TYPE: sourceRecord,
                  CALL_WORKFLOW: null,
                  CALL_WORKFLOW_ACCOUNT: null,
                  CALL_WORKFLOW_GROUP: null,
                  OUTBOUND_NAME: null,
                  WFITEM: null,
                  WF_ITEM_ID: null,
                  WF_ITEM_LOCATION: null,
                  MAPNAME: null,
                  WF_ITEM_DEPENDENCY_TIMEOUT: null,
                  VALUE: null,
                  INBOUND_PATTERN: null,
                  SOURCE_FILE_DIRECTORY: e.target.value,
                  SOURCE_FILE_MASK: sourceFileMask
                };
                valuesHandler(values);
              }}
            />
            <Button
              disabled={ensureBoolean(disabled)}
              variant="outlined"
              onClick={() => {
                setSourceDirectoryModal({ open: true, isFirstTime: true });
              }}>
              <MoreHorizOutlined />
            </Button>
          </div>
          <div style={{ display: "flex", gap: 5 }}>
            <TextField
              fullWidth
              size="small"
              label={t("LBL_SOURCE_FILE_MASK")}
              disabled={ensureBoolean(disabled)}
              value={sourceFileMask}
              onChange={(e) => {
                setSourceFileMask(e.target.value);
                const values = {
                  RECORD_TYPE: sourceRecord,
                  CALL_WORKFLOW: null,
                  CALL_WORKFLOW_ACCOUNT: null,
                  CALL_WORKFLOW_GROUP: null,
                  OUTBOUND_NAME: null,
                  WFITEM: null,
                  WF_ITEM_ID: null,
                  WF_ITEM_LOCATION: null,
                  MAPNAME: null,
                  WF_ITEM_DEPENDENCY_TIMEOUT: null,
                  VALUE: null,
                  INBOUND_PATTERN: null,
                  SOURCE_FILE_DIRECTORY: sourceFileDirectory,
                  SOURCE_FILE_MASK: e.target.value
                };
                valuesHandler(values);
              }}
            />
            <Button
              disabled={ensureBoolean(disabled)}
              variant="outlined"
              onClick={() => {
                setSourceFileMaskModal({ open: true, isFirstTime: true });
              }}>
              <MoreHorizOutlined />
            </Button>
          </div>
          <Box sx={{ display: "flex", gap: 1 }}>
            <LabeledCheckbox handleCheck={(val) => {
              setPreserveDirectoryStructure(val);
              valuesHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW: null,
                CALL_WORKFLOW_ACCOUNT: null,
                CALL_WORKFLOW_GROUP: null,
                OUTBOUND_NAME: null,
                WFITEM: null,
                WF_ITEM_ID: null,
                WF_ITEM_LOCATION: null,
                MAPNAME: null,
                WF_ITEM_DEPENDENCY_TIMEOUT: null,
                VALUE: null,
                INBOUND_PATTERN: null,
                SOURCE_FILE_DIRECTORY: sourceFileDirectory,
                SOURCE_FILE_MASK: sourceFileMask,
                SOURCE_FILE_PRESERVE_PATHS: val ? "TRUE" : "FALSE",
                SOURCE_FILE_DO_NOT_DELETE: doNotDelete ? "TRUE" : "FALSE",
                FILE_COUNT_FILTER: fileCountFilter || null
              });
            }} checked={preserveDirectoryStructure} label={t("BTN_PRESERVE_DIRECTORY_STRUCTURE")} />
            <LabeledCheckbox handleCheck={(val) => {
              setDoNotDelete(val);
              valuesHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW: null,
                CALL_WORKFLOW_ACCOUNT: null,
                CALL_WORKFLOW_GROUP: null,
                OUTBOUND_NAME: null,
                WFITEM: null,
                WF_ITEM_ID: null,
                WF_ITEM_LOCATION: null,
                MAPNAME: null,
                WF_ITEM_DEPENDENCY_TIMEOUT: null,
                VALUE: null,
                INBOUND_PATTERN: null,
                SOURCE_FILE_DIRECTORY: sourceFileDirectory,
                SOURCE_FILE_MASK: sourceFileMask,
                SOURCE_FILE_PRESERVE_PATHS: preserveDirectoryStructure ? "TRUE" : "FALSE",
                SOURCE_FILE_DO_NOT_DELETE: val ? "TRUE" : "FALSE",
                FILE_COUNT_FILTER: fileCountFilter || null
              });
            }} checked={doNotDelete} label={t("BTN_DO_NOT_DELETE")} />
          </Box>
          <TextField
            fullWidth
            size="small"
            label={t("LBL_FILE_COUNT_FILTER")}
            disabled={ensureBoolean(disabled)}
            value={fileCountFilter}
            onChange={(e) => {
              // Only allow numeric input and validate maximum value
              const newValue = e.target.value.replace(/[^0-9]/g, '');
              // Check if the value exceeds the maximum allowed
              const isExceedingLimit = parseInt(newValue, 10) > 1000000;
              // Set error state if needed
              if (isExceedingLimit && newValue !== '') {
                e.target.setCustomValidity(t("BOX_FILE_COUNT_DIGITS_ONLY_"));
                e.target.reportValidity();
                return;
              } else {
                e.target.setCustomValidity('');
              }
              setFileCountFilter(newValue);
              valuesHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW: null,
                CALL_WORKFLOW_ACCOUNT: null,
                CALL_WORKFLOW_GROUP: null,
                OUTBOUND_NAME: null,
                WFITEM: null,
                WF_ITEM_ID: null,
                WF_ITEM_LOCATION: null,
                MAPNAME: null,
                WF_ITEM_DEPENDENCY_TIMEOUT: null,
                VALUE: null,
                INBOUND_PATTERN: null,
                SOURCE_FILE_DIRECTORY: sourceFileDirectory,
                SOURCE_FILE_MASK: sourceFileMask,
                SOURCE_FILE_PRESERVE_PATHS: preserveDirectoryStructure ? "TRUE" : "FALSE",
                SOURCE_FILE_DO_NOT_DELETE: doNotDelete ? "TRUE" : "FALSE",
                FILE_COUNT_FILTER: newValue || null
              });
            }}
          />
        </> : <></>
      }
      
      <WorkflowSessionModal
        valueLabel={t("LBL_SOURCE_FILE_DIRECTORY")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={sourceDirectoryModal}
        setModalOpen={setSourceDirectoryModal}
        handleClose={() => setSourceDirectoryModal({ open: false, isFirstTime: false })}
        initialValue={sourceFileDirectory}
        valuesHandler={(value) => {
          setSourceFileDirectory(value);
          valuesHandler({
            RECORD_TYPE: sourceRecord,
            CALL_WORKFLOW: null,
            CALL_WORKFLOW_ACCOUNT: null,
            CALL_WORKFLOW_GROUP: null,
            OUTBOUND_NAME: null,
            WFITEM: null,
            WF_ITEM_ID: null,
            WF_ITEM_LOCATION: null,
            MAPNAME: null,
            WF_ITEM_DEPENDENCY_TIMEOUT: null,
            VALUE: null,
            INBOUND_PATTERN: null,
            SOURCE_FILE_DIRECTORY: value,
            SOURCE_FILE_MASK: sourceFileMask
          });
        }}
      />
      <WorkflowSessionModal
        valueLabel={t("LBL_SOURCE_FILE_MASK")}
        showAccountData={true}
        showCallingWorkflow={true}
        showDateAndTime={true}
        showSystemData={true}
        showWorkflowSession={true}
        openModal={sourceFileMaskModal}
        setModalOpen={setSourceFileMaskModal}
        handleClose={() => setSourceFileMaskModal({ open: false, isFirstTime: false })}
        initialValue={sourceFileMask}
        valuesHandler={(value) => {
          setSourceFileMask(value);
          valuesHandler({
            RECORD_TYPE: sourceRecord,
            CALL_WORKFLOW: null,
            CALL_WORKFLOW_ACCOUNT: null,
            CALL_WORKFLOW_GROUP: null,
            OUTBOUND_NAME: null,
            WFITEM: null,
            WF_ITEM_ID: null,
            WF_ITEM_LOCATION: null,
            MAPNAME: null,
            WF_ITEM_DEPENDENCY_TIMEOUT: null,
            VALUE: null,
            INBOUND_PATTERN: null,
            SOURCE_FILE_DIRECTORY: sourceFileDirectory,
            SOURCE_FILE_MASK: value
          });
        }}
      />
      <WorkflowSessionModal1
        sourceType={'callingWorkflow'}
        openModal={workflowSessionModal}
        handleClose={setWorkflowSessionModal}
        valueHandler={([value]) => {
          valuesHandler({
            CALL_WORKFLOW: value?.template,
            CALL_WORKFLOW_ACCOUNT: value?.account,
            CALL_WORKFLOW_GROUP: value?.workflowGroup,
            OUTBOUND_NAME: value?.condition || null,
            RECORD_TYPE: sourceRecord,
            WFITEM: value?.parent,
            WF_ITEM_ID: value?.WF_ITEM_ID ? `${value.WF_ITEM_ID}` : null,
            WF_ITEM_LOCATION: value?.parent,
            MAPNAME: value?.MAPNAME
          });
          
          // delay to ensure the workflowItem is set and values are updated in main component
          setTimeout(() => {
            setOutboundItemsOptions([
              <MenuItem value={value?.condition}>
                {value?.condition}
              </MenuItem>
            ]);
            setWorkflowItemsOptions([
              {
                label: value?.template + "\\" + value?.parent,
                value: value?.template + "\\" + value?.parent,
              }
            ]);
            setWorkflowItem({
              label: value?.template + "\\" + value?.parent,
              value: value?.template + "\\" + value?.parent,
            });
            setSourceName(value?.MAPNAME);
          })
          const isTranslated = !!value?.condition;
          setEnableTranslatedData(isTranslated);
          setOutboundCondition(value?.condition || "");

        }}
        selection={"single"}
      />
      {sourceRecord === "WF_ITEM_DEPENDENCY" && showTimeout ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            type="number"
            value={timeout}
            disabled={ensureBoolean(disabled)}
            onChange={(e) => {
              const newTimeout = e.target.value;
              setTimeoutValue(newTimeout);
              debounceHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.title
                      : null,
                CALL_WORKFLOW_ACCOUNT:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_ACCOUNT
                    : sourceRecord === "SESSION_DATA"
                      ? account
                      : null,
                CALL_WORKFLOW_GROUP:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_GROUP
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.workflowGroup
                      : null,
                OUTBOUND_NAME: outboundCondition || null,
                WFITEM: workflowItem?.value || null,
                WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                  ? `${workflowItem.WF_ITEM_ID}`
                  : null,
                WF_ITEM_LOCATION: workflowItem?.value || null,
                MAPNAME: sourceName,
                WF_ITEM_DEPENDENCY_TIMEOUT:
                  newTimeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
                VALUE: null,
                INBOUND_PATTERN: null,
              });
            }}
            size="small"
            label={t("LBL_TIMEOUT")}
            fullWidth
          />
          <Button
            disabled={ensureBoolean(disabled)}
            variant="outlined"
            onClick={() => {
              setTimeoutModal({ open: true, isFirstTime: true });
            }}>
            <MoreHorizOutlined />
          </Button>
          <WorkflowSessionModal
            valueLabel={t("LBL_TIMEOUT")}
            showAccountData={true}
            showCallingWorkflow={true}
            showDateAndTime={true}
            showSystemData={true}
            showWorkflowSession={true}
            openModal={timeoutModal}
            setModalOpen={setTimeoutModal}
            handleClose={() =>
              setTimeoutModal({ open: false, isFirstTime: false })
            }
            initialValue={timeout}
            valuesHandler={(value) => {
              setTimeoutValue(value);
              valuesHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW
                    : null,
                CALL_WORKFLOW_ACCOUNT:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_ACCOUNT
                    : null,
                CALL_WORKFLOW_GROUP:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_GROUP
                    : null,
                OUTBOUND_NAME: outboundCondition || null,
                WFITEM: workflowItem?.value || null,
                WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                  ? `${workflowItem.WF_ITEM_ID}`
                  : null,
                WF_ITEM_LOCATION: workflowItem?.value || null,
                MAPNAME: sourceName,
                WF_ITEM_DEPENDENCY_TIMEOUT: value,
                VALUE: null,
                INBOUND_PATTERN: null,
              });
            }}
          />
        </Box>
      ) : (
        ""
      )}
      {sourceRecord === "CONSTANT" ? (
        <>
          <TextField
            disabled={ensureBoolean(disabled)}
            value={constantValue}
            onChange={(e) => {
              const newValue = e.target.value;
              setConstantValue(newValue);
              debounceHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.title
                      : null,
                CALL_WORKFLOW_ACCOUNT:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_ACCOUNT
                    : sourceRecord === "SESSION_DATA"
                      ? account
                      : null,
                CALL_WORKFLOW_GROUP:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_GROUP
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.workflowGroup
                      : null,
                OUTBOUND_NAME: outboundCondition || null,
                WFITEM: workflowItem?.value || null,
                WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                  ? `${workflowItem.WF_ITEM_ID}`
                  : null,
                WF_ITEM_LOCATION: workflowItem?.value || null,
                MAPNAME: sourceName,
                WF_ITEM_DEPENDENCY_TIMEOUT:
                  timeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
                VALUE: newValue || node?.VALUE || null,
                INBOUND_PATTERN: null,
              });
            }}
            size="small"
            label={t("VALUE")}
            fullWidth
          />
          <PatternControl
            disabled={ensureBoolean(disabled)}
            inputId="fieldDecPattern"
            inputLabel={t("LBL_INBOUND_DATE_PATTERN")}
            pattern={inboundPattern}
            setPattern={(val) => {
              setInboundPattern(val);
              debounceHandler({
                RECORD_TYPE: sourceRecord,
                CALL_WORKFLOW:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.title
                      : null,
                CALL_WORKFLOW_ACCOUNT:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_ACCOUNT
                    : sourceRecord === "SESSION_DATA"
                      ? account
                      : null,
                CALL_WORKFLOW_GROUP:
                  sourceRecord === "CALL_WORKFLOW_DATA"
                    ? node?.CALL_WORKFLOW_GROUP
                    : sourceRecord === "SESSION_DATA"
                      ? leftTreeSelectedNode?.workflowGroup
                      : null,
                OUTBOUND_NAME: outboundCondition || null,
                WFITEM: workflowItem?.value || null,
                WF_ITEM_ID: workflowItem?.WF_ITEM_ID
                  ? `${workflowItem.WF_ITEM_ID}`
                  : null,
                WF_ITEM_LOCATION: workflowItem?.value || null,
                MAPNAME: sourceName,
                WF_ITEM_DEPENDENCY_TIMEOUT:
                  timeout || node?.WF_ITEM_DEPENDENCY_TIMEOUT || null,
                VALUE: node?.VALUE || null,
                INBOUND_PATTERN: val || node?.INBOUND_PATTERN || null,
              });
            }}
          />
        </>
      ) : (
        ""
      )}
    </Box>
  );
};

export default WorkflowSourceType;
